const API_BASE_URL = 'https://api.disloja.com.br/transport/route';

const THUMBS_UP_ICON = `
  <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M12 18V32H8C6.89543 32 6 31.1046 6 30V20C6 18.8954 6.89543 18 8 18H12Z" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/>
    <path d="M12 18L16 8C16.5523 6.66667 18 6 19.5 6C21.5 6 23 7.5 23 9.5V14H30C32.2091 14 34 15.7909 34 18V19C34 20.1046 33.5523 21.5523 32.8284 22.8284L28 30V32H14" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>
`;

const THUMBS_DOWN_ICON = `
  <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M12 22V8H8C6.89543 8 6 8.89543 6 10V20C6 21.1046 6.89543 22 8 22H12Z" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/>
    <path d="M12 22L16 32C16.5523 33.3333 18 34 19.5 34C21.5 34 23 32.5 23 30.5V26H30C32.2091 26 34 24.2091 34 22V21C34 19.8954 33.5523 18.4477 32.8284 17.1716L28 10V8H14" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>
`;

/**
 * @param {string} value
 * @returns {string}
 */
function normalizeCep(value) {
  return String(value || '').replace(/\D/g, '');
}

/**
 * @param {string} cep
 * @returns {boolean}
 */
function isValidCep(cep) {
  return cep.length === 8;
}

/**
 * @param {string} cep
 * @returns {Promise<{ ok: true, data: object } | { ok: false, message: string, unavailable?: boolean }>}
 */
async function fetchMountingRoute(cep) {
  try {
    const response = await fetch(`${API_BASE_URL}/${cep}`);

    if (response.status === 400) {
      return { ok: false, message: 'CEP inválido. Verifique o número informado e tente novamente.' };
    }

    if (response.status === 404) {
      return {
        ok: false,
        unavailable: true,
        message: 'Infelizmente não oferecemos serviço de montagem para este CEP.',
      };
    }

    if (!response.ok) {
      return { ok: false, message: 'Não foi possível verificar a disponibilidade. Tente novamente em alguns instantes.' };
    }

    const data = await response.json();
    return { ok: true, data };
  } catch {
    return {
      ok: false,
      message: 'Não foi possível verificar a disponibilidade no momento. Verifique sua conexão e tente novamente.',
    };
  }
}

/**
 * @param {object} data
 * @returns {boolean}
 */
function isMountingAvailable(data) {
  return data?.isActive === true && data?.isMounting === true;
}

/**
 * @returns {string}
 */
function buildSuccessMessage() {
  return 'Temos serviço de montagem disponível para seu CEP.';
}

if (!customElements.get('mounting-availability-checker')) {
  class MountingAvailabilityChecker extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-mounting-form]');
      this.input = this.form?.querySelector('input[name="zip-code"]');
      this.submitButton = this.form?.querySelector('.mounting-availability__submit');
      this.errorMessage = this.querySelector('[data-error-message]');
      this.formView = this.querySelector('[data-form-view]');
      this.resultView = this.querySelector('[data-result-view]');
      this.resultIcon = this.querySelector('[data-result-icon]');
      this.resultMessage = this.querySelector('[data-result-message]');
      this.backButton = this.querySelector('[data-back-button]');
      this.dialogComponent = this.closest('dialog-component');

      this.bindEvents();
    }

    bindEvents() {
      if (!this.form || !this.input) return;

      this.form.addEventListener('submit', this.handleSubmit);
      this.input.addEventListener('input', this.handleInput);
      this.backButton?.addEventListener('click', this.showFormView);

      this.dialogComponent?.addEventListener('dialog:close', this.resetView);
    }

    disconnectedCallback() {
      this.form?.removeEventListener('submit', this.handleSubmit);
      this.input?.removeEventListener('input', this.handleInput);
      this.backButton?.removeEventListener('click', this.showFormView);
      this.dialogComponent?.removeEventListener('dialog:close', this.resetView);
    }

    /** @param {Event} event */
    handleSubmit = async (event) => {
      event.preventDefault();

      const cep = normalizeCep(this.input?.value);

      if (this.input) {
        this.input.value = cep;
      }

      if (!isValidCep(cep)) {
        this.showError('Por favor, informe um CEP válido com 8 dígitos.');
        return;
      }

      this.hideError();
      this.setLoading(true);

      const result = await fetchMountingRoute(cep);

      this.setLoading(false);

      if (!result.ok) {
        if (result.unavailable) {
          this.showResult('unavailable', result.message);
          return;
        }

        this.showError(result.message);
        return;
      }

      if (isMountingAvailable(result.data)) {
        this.showResult('success', buildSuccessMessage());
        return;
      }

      this.showResult(
        'unavailable',
        'Infelizmente não oferecemos serviço de montagem para este CEP.'
      );
    };

    handleInput = () => {
      this.hideError();
    };

    /** @param {string} message */
    showError(message) {
      if (!this.errorMessage) return;

      this.errorMessage.textContent = message;
      this.errorMessage.classList.remove('hidden');
    }

    hideError() {
      if (!this.errorMessage) return;

      this.errorMessage.textContent = '';
      this.errorMessage.classList.add('hidden');
    }

    /** @param {'success' | 'unavailable'} type @param {string} message */
    showResult(type, message) {
      if (!this.formView || !this.resultView || !this.resultIcon || !this.resultMessage) return;

      this.formView.hidden = true;
      this.formView.classList.add('hidden');

      this.resultView.hidden = false;
      this.resultView.classList.remove('hidden');
      this.resultView.classList.remove('mounting-availability__result--success', 'mounting-availability__result--unavailable');
      this.resultView.classList.add(`mounting-availability__result--${type}`);

      this.resultIcon.innerHTML = type === 'success' ? THUMBS_UP_ICON : THUMBS_DOWN_ICON;
      this.resultIcon.style.color = type === 'success' ? '#7ed321' : '#e8631a';
      this.resultMessage.textContent = message;
    }

    showFormView = () => {
      if (!this.formView || !this.resultView) return;

      this.formView.hidden = false;
      this.formView.classList.remove('hidden');

      this.resultView.hidden = true;
      this.resultView.classList.add('hidden');
      this.resultView.classList.remove('mounting-availability__result--success', 'mounting-availability__result--unavailable');

      this.hideError();
      this.setLoading(false);
      this.input?.focus();
    };

    resetView = () => {
      this.showFormView();

      if (this.input) {
        this.input.value = '';
      }
    };

    /** @param {boolean} isLoading */
    setLoading(isLoading) {
      if (!this.submitButton) return;

      this.submitButton.disabled = isLoading;
      this.submitButton.classList.toggle('is-loading', isLoading);
    }
  }

  customElements.define('mounting-availability-checker', MountingAvailabilityChecker);
}
